/**
 * Public Hot Streak Leaderboard — marketing page.
 * Only shows users who opted-in to public visibility. Raw send / reply counts
 * are HIDDEN here by the backend (anti-gaming guardrail).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Flame, Trophy, MapPin, ArrowUpRight, Loader2 } from "lucide-react";

const WINDOWS = [
  { days: 7,  label: "Last 7 days"  },
  { days: 30, label: "Last 30 days" },
  { days: 90, label: "Last 90 days" },
];

const RANK_BG = {
  1: "linear-gradient(120deg, #F59E0B 0%, #FB923C 35%, #EF4444 100%)",
  2: "linear-gradient(120deg, #94A3B8 0%, #CBD5E1 100%)",
  3: "linear-gradient(120deg, #92400E 0%, #B45309 100%)",
};

export default function PublicStreaks() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/public/leaderboard/streaks`, { params: { days } })
      .then((r) => setData(r.data))
      .catch(() => setData({ leaderboard: [], total_qualified: 0 }))
      .finally(() => setLoading(false));
  }, [days]);

  return (
    <div className="min-h-screen bg-[#0B132B] text-white" data-testid="public-streaks-page">
      {/* Header strip */}
      <header className="border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-display font-extrabold text-xl">
            <span className="w-7 h-7 rounded bg-emerald-500 flex items-center justify-center text-[#0B132B] text-sm font-black">A</span>
            iSpeedToClose
          </Link>
          <Link
            to="/register"
            className="text-xs uppercase tracking-wider font-bold bg-emerald-500 text-[#0B132B] px-4 py-2 rounded-full hover:bg-emerald-400"
            data-testid="public-streaks-cta"
          >
            Start free
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-4xl mx-auto px-6 pt-16 pb-10 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-500/10 border border-orange-500/20 text-orange-300 text-xs uppercase tracking-wider font-bold mb-5">
          <Flame size={13} fill="currentColor" /> Hot Streak Leaderboard
        </div>
        <h1 className="font-display font-black text-4xl md:text-6xl tracking-tight">
          The wholesalers your <span className="text-emerald-400">buyers</span> actually reply&nbsp;to.
        </h1>
        <p className="text-white/60 text-base md:text-lg mt-5 max-w-2xl mx-auto leading-relaxed">
          A live ranking of iSpeedToClose users by 30-day buyer reply rate. Only those who chose to be listed publicly appear here.
          Raw deal volume is private — we publish the rate, not the book size.
        </p>
        <div className="flex justify-center gap-2 mt-7">
          {WINDOWS.map((w) => (
            <button
              key={w.days}
              type="button"
              className={"px-4 py-2 rounded-full text-xs uppercase tracking-wider font-bold transition " + (days === w.days ? "bg-white text-[#0B132B]" : "bg-white/5 text-white/70 hover:bg-white/10")}
              onClick={() => setDays(w.days)}
              data-testid={`public-streaks-window-${w.days}`}
            >
              {w.label}
            </button>
          ))}
        </div>
      </section>

      {/* Board */}
      <section className="max-w-4xl mx-auto px-6 pb-16">
        {loading ? (
          <div className="text-center py-16 text-white/50">
            <Loader2 size={24} className="inline animate-spin" /> Loading streaks…
          </div>
        ) : (data?.leaderboard?.length ?? 0) === 0 ? (
          <div className="text-center py-16">
            <Flame size={36} className="mx-auto text-orange-400/60 mb-3" />
            <div className="font-display font-bold text-xl mb-2">
              Nobody&rsquo;s publicly on the board yet
            </div>
            <p className="text-white/60 text-sm max-w-md mx-auto">
              The current window has no agents opted into public visibility.
              Run iSpeedToClose, send a few nudges, and own this page.
            </p>
            <Link
              to="/register"
              className="inline-block mt-5 bg-emerald-500 text-[#0B132B] font-bold px-6 py-3 rounded-full hover:bg-emerald-400"
            >
              Start your streak →
            </Link>
          </div>
        ) : (
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur overflow-hidden" data-testid="public-streaks-table">
            {data.leaderboard.map((row) => <PublicRow key={row.rank} row={row} />)}
          </div>
        )}

        <div className="mt-8 text-center text-xs text-white/40 max-w-xl mx-auto leading-relaxed">
          Ranking criteria: minimum {data?.min_to_qualify ?? 10} buyer nudges sent in the window.
          Listed in descending order by reply rate. Identity shown only with each agent&rsquo;s explicit consent.
        </div>
      </section>

      {/* Footer CTA */}
      <section className="border-t border-white/10">
        <div className="max-w-4xl mx-auto px-6 py-10 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-center md:text-left">
            <div className="font-display font-extrabold text-2xl">Want your name up there?</div>
            <p className="text-white/60 text-sm mt-1">
              Run iSpeedToClose, ship a few deals, opt-in from Settings. Free to start.
            </p>
          </div>
          <Link
            to="/register"
            className="bg-emerald-500 text-[#0B132B] font-bold px-6 py-3 rounded-full hover:bg-emerald-400 inline-flex items-center gap-1.5"
          >
            Start free <ArrowUpRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}

function PublicRow({ row }) {
  const isPodium = row.rank <= 3;
  const rankBg = RANK_BG[row.rank] || "transparent";
  return (
    <div
      className="flex items-center gap-4 px-5 py-5 border-b border-white/10 last:border-b-0"
      data-testid={`public-streaks-row-${row.rank}`}
    >
      <div
        className={"w-12 h-12 rounded-full flex items-center justify-center font-extrabold shrink-0 " + (isPodium ? "text-white" : "bg-white/5 text-white/70 border border-white/10")}
        style={isPodium ? { background: rankBg } : undefined}
      >
        {isPodium ? <Trophy size={17} fill="currentColor" /> : <span className="text-base">{row.rank}</span>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-bold text-base truncate">{row.display_name}</span>
          {row.company && (
            <span
              className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full text-white"
              style={{ background: row.brand_accent || "#10B981" }}
            >
              {row.company}
            </span>
          )}
        </div>
        {row.metro && (
          <div className="text-xs text-white/50 mt-1 flex items-center gap-1">
            <MapPin size={11} /> {row.metro}
          </div>
        )}
      </div>
      <div className="shrink-0 text-right">
        <div className="font-display font-black text-3xl tabular-nums text-emerald-400">
          {row.reply_rate}%
        </div>
        <div className="text-[10px] uppercase tracking-wider text-white/40">
          reply rate
        </div>
      </div>
    </div>
  );
}
